<template>
  <FlexCol gap-4>
    <Grid gap-3 cols-3 class="pb-3 border-b">
      <div>
        <div class="text-gray-500 text-sm">Grupo</div>
        <div>{{ item?.serviceGroup ?? '-' }}</div>
      </div>
      <div>
        <div class="text-gray-500 text-sm">Setor</div>
        <div>{{ item?.sectorName ?? '-' }}</div>
      </div>
      <div>
        <div class="text-gray-500 text-sm">Tipo de Equipe</div>
        <div>{{ item?.teamType ?? '-' }}</div>
      </div>
    </Grid>

    <Accordion title="Serviços Secundários">
      <TableFull :headers="headersSecondary" :items="secondaryItems" v-model="tableSecondary" offline />
    </Accordion>

    <Accordion title="MOS Serviços Relacionados">
      <FlexCol gap-3>
        <div class="text-lg">Áreas Comuns</div>
        <TableFull :headers="headersMosComputed" :items="mosRelatedCommonWithPrices" v-model="tableMosRelatedCommon" offline />

        <div class="text-lg">Áreas de Complexidade</div>
        <TableFull :headers="headersMosComputed" :items="mosRelatedComplexityWithPrices" v-model="tableMosRelatedComplexity" offline />
      </FlexCol>
    </Accordion>

    <Accordion title="MOS de Deslocamentos">
      <FlexCol gap-3>
        <div class="text-lg">Áreas Comuns</div>
        <TableFull :headers="headersMovementMosComputed" :items="mosMovementCommonWithPrices" v-model="tableMosMovementCommon" offline />

        <div class="text-lg">Áreas de Complexidade</div>
        <TableFull :headers="headersMovementMosComputed" :items="mosMovementComplexityWithPrices" v-model="tableMosMovementComplexity" offline />
      </FlexCol>
    </Accordion>
  </FlexCol>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { get } from '@/crud'
import { useFormat } from '@/composables/useFormat'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  priceTableId: {
    type: [String, Number],
    default: null,
  },
})

const { formatMoneyS } = useFormat()

const headersSecondary = ref([
  { title: 'Código', field: 'externalCode' },
  { title: 'Descrição', field: 'name' },
  { title: 'Justificativa', field: 'glosa' },
])

const headersMos = ref([
  { title: 'Código', field: 'idMos' },
  { title: 'Descrição', field: 'title' },
  { title: 'Pontos', field: 'points' },
])

const headersMovementMos = ref([
  { title: 'Distrito', field: 'district' },
  { title: 'Código', field: 'idMos' },
  { title: 'Descrição', field: 'title' },
  { title: 'Pontos', field: 'points' },
])

const tableSecondary = ref({ page: 1, total: 0, limit: 50, pages: 0, order: [] })
const tableMosRelatedCommon = ref({ page: 1, total: 0, limit: 50, pages: 0, order: [] })
const tableMosRelatedComplexity = ref({ page: 1, total: 0, limit: 50, pages: 0, order: [] })
const tableMosMovementCommon = ref({ page: 1, total: 0, limit: 50, pages: 0, order: [] })
const tableMosMovementComplexity = ref({ page: 1, total: 0, limit: 50, pages: 0, order: [] })

const secondaryItems = ref([])
const mosRelatedCommon = ref([])
const mosRelatedComplexity = ref([])
const mosMovementCommon = ref([])
const mosMovementComplexity = ref([])
const selectedPriceTable = ref(null)

const normalizeMovementRows = (rows = []) => {
  return rows.map((row) => ({ ...row, district: row.district ?? '0000' }))
}

const getAdjustmentSignal = (type) => {
  const normalized = String(type ?? '').toLowerCase()
  if (normalized.includes('desconto')) return -1
  if (normalized.includes('bonificacao') || normalized.includes('bonificação')) return 1
  return 0
}

const applyAdjustment = (basePrice, adjustment) => {
  const signal = getAdjustmentSignal(adjustment?.type)
  const value = Number(adjustment?.value) || 0
  const base = Number(basePrice) || 0
  if (!signal) return base
  return Number((base + (base * value * signal) / 100).toFixed(4))
}

const dynamicPriceHeaders = computed(() => {
  if (!props.priceTableId) return []
  return [{ title: 'Preço', field: 'price', format: formatMoneyS }]
})

const composeHeaders = (baseHeaders) => [...baseHeaders, ...dynamicPriceHeaders.value]

const headersMosComputed = computed(() => composeHeaders(headersMos.value))
const headersMovementMosComputed = computed(() => composeHeaders(headersMovementMos.value))

const enrichMosRows = (rows = []) => {
  if (!props.priceTableId || !selectedPriceTable.value) return rows

  const priceRows = Array.isArray(selectedPriceTable.value?.prices) ? selectedPriceTable.value.prices : []
  const adjustments = Array.isArray(selectedPriceTable.value?.items) ? selectedPriceTable.value.items : []
  const pricesByMos = priceRows.reduce((acc, item) => {
    acc[String(item.idMos ?? '').trim()] = item
    return acc
  }, {})

  return rows.map((row) => {
    const priceRow = pricesByMos[String(row.idMos ?? '').trim()]
    if (!priceRow) {
      return row
    }

    const nextRow = { ...row }
    const basePrice = Number(priceRow.originalPrice ?? priceRow.price ?? 0)

    if (Array.isArray(priceRow.history) && priceRow.history.length > 0) {
      const lastHistory = [...priceRow.history]
        .filter((historyItem) => Number.isFinite(Number(historyItem.sequence)) && Number(historyItem.sequence) > 0)
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .at(-1)

      nextRow.price = Number(lastHistory?.newPrice ?? basePrice)
      return nextRow
    }

    let currentPrice = basePrice
    adjustments.forEach((adjustment, index) => {
      currentPrice = applyAdjustment(currentPrice, adjustment)
    })

    nextRow.price = currentPrice

    return nextRow
  })
}

const mosRelatedCommonWithPrices = computed(() => enrichMosRows(mosRelatedCommon.value))
const mosRelatedComplexityWithPrices = computed(() => enrichMosRows(mosRelatedComplexity.value))
const mosMovementCommonWithPrices = computed(() => enrichMosRows(mosMovementCommon.value))
const mosMovementComplexityWithPrices = computed(() => enrichMosRows(mosMovementComplexity.value))

const loadSecondary = async (idService) => {
  const response = await get(`service/${idService}/secondary`)
  secondaryItems.value = response?.error ? [] : response.data || []
}

const loadMos = async (idService, movementFlag, complexityAreaFlag) => {
  const response = await get(`service/${idService}/mos`, {
    params: { movementFlag, complexityAreaFlag },
  })
  if (response?.error) {
    return []
  }
  return response.data || []
}

const loadSelectedPriceTable = async () => {
  if (!props.priceTableId) {
    selectedPriceTable.value = null
    return
  }

  const response = await get('priceTable', {
    params: {
      page: 1,
      limit: 1,
      filters: [{ column: 'id', value: Number(props.priceTableId) }],
    },
  })

  selectedPriceTable.value = Array.isArray(response.data) ? response.data[0] ?? null : null
}

const loadAll = async () => {
  const idService = Number(props.item?.id || 0)
  if (!idService) {
    secondaryItems.value = []
    mosRelatedCommon.value = []
    mosRelatedComplexity.value = []
    mosMovementCommon.value = []
    mosMovementComplexity.value = []
    return
  }

  await loadSecondary(idService)
  mosRelatedCommon.value = await loadMos(idService, 0, 0)
  mosRelatedComplexity.value = await loadMos(idService, 0, 1)
  mosMovementCommon.value = normalizeMovementRows(await loadMos(idService, 1, 0))
  mosMovementComplexity.value = normalizeMovementRows(await loadMos(idService, 1, 1))
}

watch(
  () => props.item?.id,
  async () => {
    await loadAll()
  },
  { immediate: true },
)

watch(
  () => props.priceTableId,
  async () => {
    await loadSelectedPriceTable()
  },
  { immediate: true },
)

onMounted(async () => {
  await loadAll()
})
</script>
